import { useState, useCallback } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject, listAll, getMetadata } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { app } from '../lib/firebase';
import { doc, getDoc, updateDoc, getFirestore, collection, query, getDocs, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface UploadProgress {
  progress: number;
  downloadUrl?: string;
  error?: string;
}

export interface StorageFileMetadata {
  name: string;
  fullPath: string;
  size: number;
  contentType?: string;
  timeCreated: string;
  updated: string;
  downloadTokens?: string[];
}

export interface FileInfo {
  name: string;
  path: string;
  size: number;
  created: Date;
  ageInDays: number;
  tier: 'hot' | 'warm' | 'cold' | 'archive';
}

export interface StorageAnalytics {
  totalFiles: number;
  totalSize: number;
  hotFiles: number;
  warmFiles: number;
  coldFiles: number;
  archiveFiles: number;
  potentialSavings: number;
  recommendedActions: string[];
  filesList: FileInfo[];
  costs: {
    current: number;
    optimized: number;
    savings: number;
    savingsPercentage: number;
  };
}

export const useStorage = () => {
  const [uploadState, setUploadState] = useState<Record<string, UploadProgress>>({});
  const storage = getStorage(app);
  const firestore = getFirestore(app);
  const functions = getFunctions(app);

  const uploadImage = useCallback(
    async (file: File, path: string = 'products'): Promise<string> => {
      console.log('uploadImage called with:', { fileName: file.name, path, fileSize: file.size });
      
      const id = uuidv4();
      
      // Ha a path már tartalmaz fájlnevet (van benne pont), akkor azt használjuk
      // Egyébként generálunk egy UUID-s fájlnevet
      let fullPath: string;
      if (path.includes('.') && path.split('/').pop()?.includes('.')) {
        // A path már tartalmaz fájlnevet
        fullPath = path;
        console.log('Using provided path with filename:', fullPath);
      } else {
        // Generálunk fájlnevet
        const extension = file.name.split('.').pop();
        const fileName = `${id}.${extension}`;
        fullPath = `${path}/${fileName}`;
        console.log('Generated new filename:', fullPath);
      }
      
      const storageRef = ref(storage, fullPath);
      console.log('Storage ref created for path:', fullPath);
      
      setUploadState(prev => ({
        ...prev,
        [id]: { progress: 0 }
      }));

      return new Promise((resolve, reject) => {
        console.log('Starting upload for:', fullPath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`Upload progress for ${fullPath}: ${progress.toFixed(2)}%`);
            setUploadState(prev => ({
              ...prev,
              [id]: { ...prev[id], progress }
            }));
          },
          (error) => {
            console.error('Firebase Storage upload error:', error);
            console.error('Error details:', { code: error.code, message: error.message });
            setUploadState(prev => ({
              ...prev,
              [id]: { ...prev[id], error: error.message }
            }));
            reject(error);
          },
          async () => {
            try {
              console.log('Upload completed, getting download URL for:', fullPath);
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
              setUploadState(prev => ({
                ...prev,
                [id]: { ...prev[id], progress: 100, downloadUrl }
              }));
              console.log('Firebase Storage upload successful:', downloadUrl);
              resolve(downloadUrl);
            } catch (error: any) {
              console.error('Firebase Storage getDownloadURL error:', error);
              setUploadState(prev => ({
                ...prev,
                [id]: { ...prev[id], error: error.message }
              }));
              reject(error);
            }
          }
        );
      });
    },
    [storage]
  );

  const getUploadProgress = useCallback(
    (id: string): UploadProgress | undefined => {
      return uploadState[id];
    },
    [uploadState]
  );

  const uploadFile = async (file: File, path: string, documentType?: string): Promise<string> => {
    const storageRef = ref(storage, path);
    await uploadBytesResumable(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    // If this is a document upload and document type is provided, update the document metadata in Firestore
    if (documentType && (path.includes('/documents/') || path.includes('/contracts/'))) {
      try {
        // Parse the path to determine collection and document ID
        // Paths are in the form: [collection]/[id]/documents/[filename]
        const pathParts = path.split('/');
        const collection = pathParts[0]; // "quotations" or "customers"
        const documentId = pathParts[1]; // The ID of the quotation or customer

        // For documents in the documents collection
        if (pathParts.length >= 4 && pathParts[2] === 'documents') {
          const docRef = doc(firestore, collection, documentId);
          const docSnapshot = await getDoc(docRef);
          
          if (docSnapshot.exists()) {
            // Get the current documents array
            const currentData = docSnapshot.data();
            const documents = currentData.documents || [];
            
            // Get filename for more precise matching
            const fileName = file.name;
            const baseName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
            
            // Find the document with matching URL, name or originalName
            const documentIndex = documents.findIndex((doc: any) => 
              doc.url === downloadURL || 
              doc.name === fileName || 
              doc.originalName === fileName ||
              (doc.name && doc.name.includes(baseName)) ||
              (doc.originalName && doc.originalName.includes(baseName))
            );
            
            if (documentIndex >= 0) {
              // Update existing document type
              documents[documentIndex].type = documentType;
              await updateDoc(docRef, { documents });
              console.log(`Document type updated to ${documentType} for ${path}`);
            } else {
              // If document wasn't found by URL or name, it might be a new document
              // The parent component should handle adding it to the documents array
              console.log(`Document not found in Firestore, parent component should add it: ${path}`);
            }
          }
        }
      } catch (error) {
        console.error('Error updating document type in Firestore:', error);
      }
    }

    return downloadURL;
  };

  const deleteFile = async (path: string): Promise<void> => {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  };

  // Rekurzív fájl listázás helper függvény
  const listAllFilesRecursively = async (storageRef: any): Promise<{ files: any[], totalSize: number }> => {
    const files: any[] = [];
    let totalSize = 0;
    
    try {
      const result = await listAll(storageRef);
      
      // Aktuális szint fájljai
      for (const itemRef of result.items) {
        try {
          const metadata = await getMetadata(itemRef);
          files.push({ ref: itemRef, metadata });
          totalSize += metadata.size || 0;
        } catch (error) {
          // Silently skip files with metadata errors
        }
      }
      
      // Rekurzív almappák feldolgozása
      for (const folderRef of result.prefixes) {
        try {
          const subResult = await listAllFilesRecursively(folderRef);
          files.push(...subResult.files);
          totalSize += subResult.totalSize;
        } catch (error) {
          // Silently skip folders with errors
        }
      }
    } catch (error) {
      // Silently handle errors
    }
    
    return { files, totalSize };
  };

  // Storage analytics és költség optimalizáció
  const getCostOptimizationData = useCallback(
    async (organizationId?: string, enableLogging: boolean = false): Promise<StorageAnalytics> => {
      try {
        // Firebase Storage gyökér referencia
        const storageRef = ref(storage, '');
        
        // Rekurzív fájl listázás
        const { files, totalSize: calculatedTotalSize } = await listAllFilesRecursively(storageRef);
        
        let totalFiles = files.length;
        let totalSize = calculatedTotalSize;
        let hotFiles = 0;
        let warmFiles = 0;
        let coldFiles = 0;
        let archiveFiles = 0;
        const filesList: FileInfo[] = [];
        
        // Fájlok kategorizálása
        for (const { ref: itemRef, metadata } of files) {
          try {
            // Fájl életkor alapján kategorizálás
            const fileAge = Date.now() - new Date(metadata.timeCreated).getTime();
            const daysOld = fileAge / (1000 * 60 * 60 * 24);
            
            let tier: 'hot' | 'warm' | 'cold' | 'archive';
            if (daysOld <= 7) {
              hotFiles++;
              tier = 'hot';
            } else if (daysOld <= 30) {
              warmFiles++;
              tier = 'warm';
            } else if (daysOld <= 90) {
              coldFiles++;
              tier = 'cold';
            } else {
              archiveFiles++;
              tier = 'archive';
            }
            
            // Fájl információk hozzáadása a listához
            filesList.push({
              name: itemRef.name,
              path: itemRef.fullPath,
              size: metadata.size || 0,
              created: new Date(metadata.timeCreated),
              ageInDays: daysOld,
              tier
            });
          } catch (error) {
            // Silently skip files with errors
          }
        }
        
        // Költségszámítás (Firebase Storage pricing)
        const standardCostPerGB = 0.026; // $0.026/GB/month
        const nearlineCostPerGB = 0.010; // $0.010/GB/month  
        const coldlineCostPerGB = 0.004; // $0.004/GB/month
        const archiveCostPerGB = 0.0012; // $0.0012/GB/month
        
        const totalSizeGB = totalSize / (1024 * 1024 * 1024);
        const currentCost = totalSizeGB * standardCostPerGB;
        
        // Optimalizált költség kalkuláció - NaN elkerülése
        let optimizedCost = 0;
        
        if (totalFiles > 0) {
          const hotSizeGB = (hotFiles / totalFiles) * totalSizeGB;
          const warmSizeGB = (warmFiles / totalFiles) * totalSizeGB;
          const coldSizeGB = (coldFiles / totalFiles) * totalSizeGB;
          const archiveSizeGB = (archiveFiles / totalFiles) * totalSizeGB;
          
          optimizedCost = 
            (hotSizeGB * standardCostPerGB) +
            (warmSizeGB * nearlineCostPerGB) +
            (coldSizeGB * coldlineCostPerGB) +
            (archiveSizeGB * archiveCostPerGB);
        } else {
          // Ha nincs fájl, akkor optimalizált költség = jelenlegi költség
          optimizedCost = currentCost;
        }
        
        const savings = Math.max(0, currentCost - optimizedCost);
        const savingsPercentage = currentCost > 0 ? (savings / currentCost) * 100 : 0;
        
        // Biztosítjuk, hogy minden érték valid szám legyen
        const safeCurrentCost = isNaN(currentCost) ? 0 : currentCost;
        const safeOptimizedCost = isNaN(optimizedCost) ? 0 : optimizedCost;
        const safeSavings = isNaN(savings) ? 0 : savings;
        const safeSavingsPercentage = isNaN(savingsPercentage) ? 0 : savingsPercentage;
        
        // Javaslatok generálása
        const recommendedActions: string[] = [];
        
        if (coldFiles > 0) {
          recommendedActions.push(`${coldFiles} fájl áthelyezhető Coldline tárolásba (90+ nap régi)`);
        }
        if (archiveFiles > 0) {
          recommendedActions.push(`${archiveFiles} fájl archiválható (365+ nap régi)`);
        }
        if (warmFiles > totalFiles * 0.3) {
          recommendedActions.push(`${warmFiles} fájl áthelyezhető Nearline tárolásba (30+ nap régi)`);
        }
        if (recommendedActions.length === 0) {
          recommendedActions.push('Minden fájl optimálisan van tárolva!');
        }
        
        const potentialSavings = Math.max(0, savings);
        
        return {
          totalFiles,
          totalSize,
          hotFiles,
          warmFiles,
          coldFiles,
          archiveFiles,
          potentialSavings: safeSavings,
          recommendedActions,
          filesList,
          costs: {
            current: safeCurrentCost,
            optimized: safeOptimizedCost,
            savings: safeSavings,
            savingsPercentage: safeSavingsPercentage
          }
        };
        
      } catch (error) {
        console.error('❌ Error analyzing storage:', error);
        
        // Fallback mock data ha hiba van
        return {
          totalFiles: 0,
          totalSize: 0,
          hotFiles: 0,
          warmFiles: 0,
          coldFiles: 0,
          archiveFiles: 0,
          potentialSavings: 0,
          recommendedActions: ['Nem sikerült elemezni a tárolást'],
          filesList: [],
          costs: {
            current: 0,
            optimized: 0,
            savings: 0,
            savingsPercentage: 0
          }
        };
      }
    },
    [storage]
  );

  const moveFileToTier = useCallback(
    async (filePath: string, targetTier: 'hot' | 'warm' | 'cold' | 'archive'): Promise<boolean> => {
      try {
        console.log(`Moving file ${filePath} to ${targetTier} tier...`);
        
        // Call Firebase Function to move file to different storage tier
        const moveFileFunction = httpsCallable(functions, 'moveFileToStorageTier');
        const result = await moveFileFunction({
          filePath,
          targetTier
        });
        
        const data = result.data as any;
        
        if (data.success) {
          console.log('File moved successfully:', data);
          return true;
        } else {
          console.error('Failed to move file:', data);
          return false;
        }
      } catch (error) {
        console.error('Error moving file to tier:', error);
        return false;
      }
    },
    [functions]
  );

  const applyLifecycleRules = useCallback(
    async (organizationId?: string, dryRun: boolean = true): Promise<string[]> => {
      try {
        const actions: string[] = [];
        
        if (dryRun) {
          // Dry run - csak szimulálás
          const data = await getCostOptimizationData(organizationId);
          
          if (data.coldFiles > 0) {
            actions.push(`[SIMULATION] ${data.coldFiles} fájl áthelyezése Coldline tárolásba`);
          }
          if (data.archiveFiles > 0) {
            actions.push(`[SIMULATION] ${data.archiveFiles} fájl archiválása`);
          }
          if (data.warmFiles > 0) {
            actions.push(`[SIMULATION] ${data.warmFiles} fájl áthelyezése Nearline tárolásba`);
          }
          
          if (actions.length === 0) {
            actions.push('[SIMULATION] Nincs szükség optimalizálásra');
          }
        } else {
          // Valós alkalmazás - ezt Firebase Functions-ben kellene implementálni
          actions.push('⚠️ Valós lifecycle rule alkalmazás csak Firebase Functions-ben támogatott');
          actions.push('💡 Használd a Google Cloud Console-t a lifecycle rules beállításához');
        }
        
        return actions;
        
      } catch (error) {
        return ['❌ Hiba történt a lifecycle rules alkalmazása során'];
      }
    },
    [getCostOptimizationData]
  );

  return {
    uploadImage,
    uploadState,
    getUploadProgress,
    uploadFile,
    deleteFile,
    getCostOptimizationData,
    applyLifecycleRules,
    moveFileToTier
  };
}; 